'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaBolt, FaWrench, FaHammer, FaTint, FaHandsHelping, FaFire,
  FaPaintBrush, FaBus, FaIndustry, FaShieldAlt, FaUsers, FaCogs,
  FaCog, FaAward, FaCheckCircle, FaMapMarkerAlt, FaPhone, FaDraftingCompass, FaTools,
  FaWhatsapp, FaEnvelope, FaArrowRight
} from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import ServicesIntro from './ServiceIntro';
import Cta from '../components/NewCallToAction';

// --- STYLE CONSTANTS ---
const BRAND_BLUE = "#3b82f6";
const DARK_SLATE = "#0f172a";
const DecorativeBar = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '32px',
    width: '100%'
  }}>
    <div style={{ width: '40px', height: '4px', background: '#0ea5e9', borderRadius: '2px' }} />
    <div style={{ width: '12px', height: '4px', background: '#22c55e', opacity: 0.3, borderRadius: '2px' }} />
    <div style={{ width: '40px', height: '4px', background: '#0ea5e9', borderRadius: '2px' }} />
  </div>
);

// --- REUSABLE SERVICE CARD ---
const ServiceCard = ({ icon, title, description, details, features, locations, color, link }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.article
      whileHover={{ y: -8 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: '#ffffff',
        border: `1px solid ${isHovered ? color + '40' : '#f1f5f9'}`,
        borderRadius: '20px',
        padding: 'clamp(20px, 4vw, 32px)', // Fluid padding based on screen size
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        height: '100%',
        boxShadow: isHovered ? `0 20px 40px ${color}15` : '0 4px 12px rgba(0,0,0,0.02)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div style={{
          width: '50px', height: '50px',
          background: isHovered ? color : `${color}10`,
          borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isHovered ? '#fff' : color, transition: 'all 0.3s ease', fontSize: '1.4rem'
        }}>
          {icon}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
          {features?.slice(0, 1).map((feat, i) => (
            <span key={i} style={{
              fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
              color: color, background: `${color}12`, padding: '4px 8px', borderRadius: '8px'
            }}>{feat}</span>
          ))}
        </div>
      </header>

      {/* SEO Tip: Use H3 for service titles within an article */}
      <h3 style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', fontWeight: 800, color: DARK_SLATE, marginBottom: '10px', lineHeight: '1.3' }}>{title}</h3>
      <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '18px', lineHeight: '1.5' }}>{description}</p>

      <ul style={{ flex: 1, marginBottom: '20px', padding: 0, listStyle: 'none' }}>
        {details?.map((detail, i) => (
          <li key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
            <FaCheckCircle style={{ color: '#22c55e', fontSize: '11px', marginTop: '4px', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#475569', lineHeight: '1.4' }}>{detail}</span>
          </li>
        ))}
      </ul>

      {link && (
        <div style={{ marginBottom: '20px' }}>
          <Link to={link} style={{ color: color, fontWeight: 700, fontSize: '13px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Explore Capability <FaArrowRight style={{ fontSize: '10px' }} />
          </Link>
        </div>
      )}

      <footer style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', paddingTop: '15px', borderTop: '1px solid #f1f5f9' }}>
        {locations?.slice(0, 3).map((loc, i) => (
          <span key={i} style={{
            fontSize: '10px', color: '#64748b', background: '#f8fafc',
            padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px'
          }}>
            <FaMapMarkerAlt style={{ fontSize: '9px', color: color }} /> {loc}
          </span>
        ))}
      </footer>

      <div style={{
        position: 'absolute', bottom: 0, left: '24px', right: '24px',
        height: isHovered ? '4px' : '0px', background: color,
        borderRadius: '4px 4px 0 0', transition: 'height 0.3s ease',
      }} />
    </motion.article>
  );
};

const ServicesPage = () => {
  const services = [
    {
      icon: <FaHammer />,
      title: 'Expert Civil Works & Manpower',
      description: 'Expert civil construction manpower and subcontracting services in Qatar. We provide skilled masons, carpenters, and steel fixers for all project phases.',
      details: [
        'Skilled and unskilled manpower supply for civil construction projects',
        'Concrete works, structural repairs, flooring, and finishing solutions',
        'Roads, paving, and civil infrastructure support',
        'Project supervision and coordination to meet deadlines',
        'Certified workforce adhering to safety and ISO standards',
        'Trusted by developers and contractors in Doha, Lusail, West Bay, and industrial zones'
      ],
      color: '#2563eb',
      features: ['Skilled Workforce', 'Full Subcontracting', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/civil-subcontracting'
    },
    {
      icon: <FaCog />,
      title: 'Professional MEP Works Manpower',
      description: 'Professional Mechanical, Electrical, and Plumbing manpower supply. Skilled and unskilled manpower for installation, maintenance, and MEP subcontracts.',
      details: [
        'Skilled electricians, plumbers, HVAC technicians, and helpers for immediate deployment',
        'Installation, testing, and maintenance of MEP systems',
        'Mechanical and electrical system upgrades for commercial and industrial facilities',
        'Emergency support and 24/7 technical assistance',
        'Certified teams following safety and ISO standards',
        'Projects covered across Doha, Lusail, West Bay, The Pearl, and industrial zones'
      ],
      color: '#16a34a',
      features: ['Skilled Manpower', 'Full MEP Services', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/mep-contracting'
    },
    {
      icon: <FaPaintBrush />,
      title: 'Finishing & Painting Works',
      description: 'High-quality finishing and painting manpower supply for residential, commercial, and industrial projects across Qatar.',
      details: [
        'Professional painters, decorators, and finishing specialists',
        'Interior and exterior painting, waterproofing, and protective coatings',
        'Surface preparation, epoxy flooring, and finishing works',
        'Project supervision ensuring consistent quality and timely delivery',
        'Certified teams adhering to safety and ISO standards',
        'Trusted by contractors and developers in Doha, Lusail, West Bay, and industrial zones'
      ],
      color: '#f97316',
      features: ['Skilled Workforce', 'Quality Finishing', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/fit-out-works'
    },
    {
      icon: <FaHammer />,
      title: 'Civil Sub-Contracting Services',
      description: 'Professional civil sub-contract works delivering skilled manpower and on-site execution for residential, commercial, and industrial construction projects across Qatar.',
      details: [
        'Skilled masons, steel fixers, carpenters, and helpers for civil subcontracts',
        'Structural concrete manpower including foundations, slabs, columns, and beams',
        'Formwork, reinforcement, and concreting certified workers',
        'Repair, refurbishment, and minor civil modification teams',
        'Certified teams operating in compliance with safety regulations and ISO standards',
        'Projects executed across Doha, Lusail, West Bay, The Pearl, and major industrial zones'
      ],
      color: '#2563eb',
      features: [' Civil Sub-Contracting', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/civil-subcontracting'

    },
    {
      icon: <FaDraftingCompass />,
      title: 'Manpower Design and Drafting',
      description: 'Professional design and drafting services providing accurate drawings and technical documentation to support civil, MEP, and construction projects across Qatar.',
      details: [
        'Preparation of detailed civil, architectural, and MEP shop drawings',
        'As-built drawings and revisions in line with site conditions',
        'Coordination drawings to support construction and sub-contract works',
        'Compliance with local authority, consultant, and project specifications',
        'Experienced drafting team using industry-standard CAD software',
        'Design and drafting support for projects across Qatar, Lusail, West Bay, and industrial zones'
      ],
      color: '#0f766e',
      features: ['Accurate Drawings', 'Technical Support', 'Standards Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/manpower-supply'
    },
    {
      icon: <FaTools />,
      title: 'Fit Out Works & Manpower',
      description: 'Complete fit out and manpower supply for interior projects. Expert execution for residential, commercial, and office fit-outs across Qatar.',
      details: [
        'Complete interior fit-out manpower supply and turnkey execution',
        'Gypsum board installers, false ceilings, and decorative wall finishes',
        'Flooring contractors for tiles, epoxy, vinyl, and carpet installation',
        'Joinery, wood works, and interior finishing manpower',
        'Coordination with civil, MEP, and design teams for smooth execution',
        'Projects delivered across Doha, Lusail, West Bay, The Pearl, and major business districts'
      ],
      color: '#7c3aed',
      features: ['Complete Fit-Out', 'Quality Finishing', 'On-Time Delivery'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Business Districts'],
      link: '/services/fit-out-works'

    },
    {
      icon: <FaWrench />,
      title: 'Maintenance & Plumbing Service',
      description: 'Reliable maintenance and plumbing services supporting residential, commercial, and industrial facilities across Qatar.',
      details: [
        'General building maintenance and corrective repair works',
        'Plumbing installation, maintenance, and repair services',
        'Water supply, drainage, and sanitary system maintenance',
        'Leak detection, emergency plumbing support, and breakdown services',
        'Preventive maintenance programs to ensure system reliability',
        'Services provided across Doha, Lusail, West Bay, The Pearl, and industrial zones'
      ],
      color: '#0ea5e9',
      features: ['Preventive Maintenance', 'Emergency Support', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/mep-contracting'
    },
    ,
    {
      icon: <FaCogs />,
      title: 'Mechanical Services Manpower',
      description: 'Professional Mechanical services delivering installation, maintenance, and support for residential, commercial, and industrial projects across Qatar.',
      details: [
        'HVAC installation, maintenance, and repair services',
        'Chilled water systems, ducting, and ventilation works',
        'Mechanical equipment installation and system upgrades',
        'Preventive and corrective mechanical maintenance services',
        'Certified technicians operating in compliance with safety and ISO standards',
        'Projects and services delivered across Doha, Lusail, West Bay, The Pearl, and industrial zones'
      ],
      color: '#22c55e',
      features: ['HVAC installation', 'Preventive Maintenance', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/mep-contracting'
    },
    {
      icon: <FaBolt />,
      title: 'Electrical Services Manpower',
      description: 'Professional electrical services delivering installation, maintenance, and technical support for residential, commercial, and industrial projects across Qatar.',
      details: [
        'Electrical installation works for residential, commercial, and industrial facilities',
        'Power distribution, cabling, and wiring systems',
        'Lighting installation, testing, and maintenance',
        'Panel boards, switchgear installation, and upgrades',
        'Preventive maintenance and emergency electrical support',
        'Services delivered across Doha, Lusail, West Bay, The Pearl, and industrial zones'
      ],
      color: '#facc15',
      features: ['Electrical Installations', 'Emergency Support', 'Safety Compliant'],
      locations: ['Doha', 'Lusail', 'The Pearl', 'West Bay', 'Industrial Zones'],
      link: '/services/mep-contracting'
    }
  ];


  const additionalServices = [
    {
      icon: <FaIndustry />,
      title: 'Epoxy & Concrete Subcontracting',
      description: 'Professional epoxy flooring and concrete repair subcontracting services for industrial, commercial, and residential projects across Qatar.',
      details: [
        'Complete epoxy flooring subcontracting for industrial, commercial, and residential buildings',
        'Concrete repair, restoration, and specialized concrete works across Doha and Qatar',
        'Durable epoxy floor coating with professional application and long-lasting finish',
        'Epoxy flooring contractors for commercial, industrial, and residential projects',
        'Concrete finishing, surface treatment, and polishing expertise',
        'Comprehensive project management from site preparation to final finishing'
      ],
      color: '#6366f1',
      features: ['Epoxy Specialists', 'Concrete Experts', 'Full Subcontracting'],
      locations: ['Industrial Areas', 'Commercial Buildings', 'Residential Projects'],
      link: '/services/fit-out-works'

    },
    {
      icon: <FaShieldAlt />,
      title: 'Waterproofing Subcontracting',
      description: 'Industrial waterproofing Qatar & waterproofing subcontracting',
      details: [
        'Complete waterproofing for all building types',
        'Industrial waterproofing in Qatar for harsh environments',
        'Commercial waterproofing in Qatar for offices and retail spaces',
        'Residential waterproofing for villas and apartments',
        'Basement, foundation, roof, and terrace systems',
        'Professional installation with long-term durability guarantees'
      ],
      color: '#059669',
      features: ['Waterproofing Experts', 'Complete Subcontracts', 'Quality Systems'],
      locations: ['Industrial Areas', 'Commercial Buildings', 'Residential Complexes'],
      link: '/services/fit-out-works'
    },
    {
      icon: <FaPaintBrush />,
      title: 'Protective Coatings Subcontracting',
      description: 'Anti-corrosion coating & protective coating subcontracting',
      details: [
        'Provide complete protective coating for industrial facilities',
        'Apply anti-corrosion coating with advanced systems',
        'Protect metal surfaces and prevent corrosion effectively',
        'Deliver industrial coatings for harsh environments and chemical exposure',
        'Offer commercial protective coatings for business and manufacturing facilities',
        'Execute specialized coating applications with performance guarantees'
      ],
      color: '#dc2626',
      features: ['Anti-corrosion Experts', 'Full Subcontracting', 'Durability'],
      locations: ['Industrial Facilities', 'Commercial Buildings', 'Manufacturing Plants'],
      link: '/services/fit-out-works'

    },
    {
      icon: <FaUsers />,
      title: 'Construction Manpower & Subcontracting',
      description: 'Skilled and unskilled construction manpower supply & subcontracting services tailored for large-scale projects.',
      details: [
        'Provide full construction manpower supply and subcontracting solutions',
        'Manage MEP and civil project maintenance and complete subcontracting',
        'Supply skilled labor for all construction project types',
        'Deploy MEP and civil specialists for mechanical, electrical, plumbing, and structural works',
        'Ensure immediate manpower deployment capability',
        'Offer 24/7 manpower availability for emergency and scheduled subcontracts'
      ],
      color: '#7c3aed',
      features: ['Skilled Manpower', 'Full Subcontracting', '24/7 Availability'],
      locations: ['All Locations', 'Industrial Areas', 'Commercial Sites', 'Residential Projects'],
      link: '/services/manpower-supply'

    },
    {
      icon: <FaCog />,
      title: 'MEP Subcontracting Services',
      description: 'Comprehensive MEP maintenance and specialist subcontracting solutions with skilled and unskilled manpower.',
      details: [
        'Deliver full MEP maintenance and turnkey subcontracting for residential, commercial, and industrial projects',
        'Deploy certified MEP specialists for complex mechanical, electrical, and plumbing systems',
        'Implement preventive maintenance and rapid emergency repair services',
        'Optimize systems to enhance energy efficiency and performance',
        'Manage projects with strict quality assurance and safety compliance',
        'Provide 24/7 expert MEP support across all construction sites'
      ],
      color: '#ec4899',
      features: ['Certified MEP Specialists', 'Turnkey Subcontracts', '24/7 Expert Support'],
      locations: ['Residential, Commercial, and Industrial Projects'],
      link: '/services/mep-contracting'

    },
    {
      icon: <FaAward />,
      title: 'ISO Certified Contractor',
      description: 'ISO certified contractor providing skilled labor and manpower supply with a focus on quality and safety.',
      details: [
        'Provide ISO-certified construction and maintenance services',
        'Supply skilled manpower for industrial, residential, and commercial projects',
        'Ensure manpower availability with certified and experienced workers',
        'Maintain quality management systems compliant with international standards',
        'Deliver professional project management and execution services',
        'Deploy certified tradesmen for all construction and maintenance needs'
      ],
      color: '#f59e0b',
      features: ['ISO Certified', 'Quality Management', 'Professional Standards'],
      locations: ['All Locations', 'Residential Projects', 'Commercial Projects', 'Industrial Sites'],
      link: '/services/civil-subcontracting'

    }
  ];
  // SEO Schema for Google (JSON-LD)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Construction Manpower Supply Qatar",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Abraj Al Dhafna",
      "address": { "@type": "PostalAddress", "addressLocality": "Doha", "addressCountry": "QA" }
    },
    "areaServed": ["Doha", "Lusail", "West Bay", "The Pearl"],
    "description": "Certified manpower supply and MEP subcontracting services in Qatar including Electricians, Plumbers, and HVAC specialists."
  };

  return (
    <main style={{ background: '#f8fafc', minHeight: '100vh', width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <title>
          Best Construction Services in Qatar | Manpower Supply & Subcontracting
        </title>

        <meta
          name="description"
          content="Top-rated construction services in Qatar: Skilled manpower supply (electricians, plumbers, masons), MEP subcontracting, civil works, and industrial maintenance. 24/7 availability in Doha, Lusail, & West Bay."
        />

        <meta name="keywords" content="construction services Qatar, manpower supply Doha, skilled labor Qatar, MEP subcontractors, civil works company, industrial maintenance Qatar, electricians Qatar, plumbers Doha, painting contractors, waterproofing services, construction subcontracting Qatar, certified tradesmen, ISO certified contractor, 24/7 manpower supply, welders Qatar, masons Qatar, construction helpers Doha, Lusail construction services" />

        <link rel="canonical" href="https://abrajaldhafna.com/services" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://abrajaldhafna.com/#website",
                "url": "https://abrajaldhafna.com/",
                "name": "Abraj Al Dhafna Trading & Contracting WLL",
                "publisher": { "@id": "https://abrajaldhafna.com/#organization" }
              },
              {
                "@type": "Organization",
                "@id": "https://abrajaldhafna.com/#organization",
                "name": "Abraj Al Dhafna Trading & Contracting WLL",
                "url": "https://abrajaldhafna.com/",
                "logo": "https://abrajaldhafna.com/logo.jpeg",
                "foundingDate": "2014",
                "telephone": "+97444170859",
                "email": "abrajaldhafna2014@gmail.com",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Doha",
                  "addressCountry": "QA"
                },
                "sameAs": [
                  "https://www.linkedin.com/company/abraj-al-dhafna",
                  "https://www.facebook.com/abrajaldhafna",
                  "https://wa.me/97455994434",
                  "https://maps.google.com/?cid=YOUR_GOOGLE_BUSINESS_CID"
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+97444170859",
                  "contactType": "customer service",
                  "areaServed": "QA",
                  "availableLanguage": ["en", "ar"]
                }
              },
              {
                "@type": "CollectionPage",
                "@id": "https://abrajaldhafna.com/services#collection",
                "url": "https://abrajaldhafna.com/services",
                "name": "Construction Services & Manpower Supply in Qatar",
                "description": "Comprehensive list of construction services including manpower supply, civil works, MEP, and industrial maintenance.",
                "isPartOf": { "@id": "https://abrajaldhafna.com/#website" },
                "about": { "@id": "https://abrajaldhafna.com/#organization" }
              },
              {
                "@type": "Service",
                "name": "Construction Manpower Supply",
                "provider": { "@id": "https://abrajaldhafna.com/#organization" },
                "areaServed": "Qatar",
                "serviceType": "Manpower Supply",
                "description": "Skilled construction manpower supply in Qatar including electricians, plumbers, masons, welders, helpers, and supervisors."
              },
              {
                "@type": "Service",
                "name": "Civil Construction Subcontracting",
                "provider": { "@id": "https://abrajaldhafna.com/#organization" },
                "areaServed": "Qatar",
                "serviceType": "Construction",
                "description": "Complete construction subcontracting services including civil works, fit-out, and finishing projects."
              },
              {
                "@type": "Service",
                "name": "MEP Works & Maintenance",
                "provider": { "@id": "https://abrajaldhafna.com/#organization" },
                "areaServed": "Qatar",
                "serviceType": "MEP Services",
                "description": "MEP specialists and subcontracting services for commercial and industrial projects in Qatar."
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://abrajaldhafna.com/services#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://abrajaldhafna.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Services",
                    "item": "https://abrajaldhafna.com/services"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      <section aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
        <h1>Construction Subcontracting & Skilled Manpower Supply Company in Qatar</h1>
        <p>
          Abraj Al Dhafna Trading & Contracting WLL provides ISO certified construction
          subcontracting and skilled manpower supply services in Qatar, including electricians,
          plumbers, masons, welders, MEP specialists, painting contractors, and industrial services
          across Doha, Lusail, West Bay, and all major project locations.
        </p>
      </section>


      {/* --- HERO SECTION --- */}
      <section style={heroStyles}>
        <div style={containerStyles}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span style={badgeStyles}>ISO CERTIFIED QATAR</span>
            <h1 style={heroTitleStyles}>Certified Construction Services & <span style={{ color: BRAND_BLUE }}>Manpower</span></h1>
            <p className="services-hero-subtitle text-white">
              Your trusted partner for skilled manpower supply, civil works, and MEP subcontracting across Doha, Lusail, and West Bay.
            </p>
            {/* <p style={heroSubtitleStyles}>Providing Qatar’s construction sector with professional, skilled tradesmen and full MEP subcontracting solutions.</p> */}
          </motion.div>
        </div>
      </section>
      <ServicesIntro />
      {/* --- SERVICES GRID --- */}
      <section style={sectionPadding}>
        <div style={containerStyles}>
          <h2 style={sectionTitleStyles}>Our <span style={{ color: BRAND_BLUE }}>Services</span></h2>
          <p style={{
            maxWidth: '800px',
            margin: '0 auto 40px', // Centers the text and adds space before the grid
            textAlign: 'center',
            color: '#64748b',      // Professional slate/grey color
            lineHeight: '1.6',
            fontSize: 'clamp(14px, 2vw, 16px)',
            padding: '0 15px'      // Prevents text touching edges on mobile
          }}>
            {"We provide expert manpower for Civil, MEP, Finishing & Painting projects, specializing in Civil Sub-Contracting, Design & Drafting, and Fit-Out works. Our teams are skilled, reliable, and committed to delivering projects on time and to the highest standards, ensuring your deadlines are always met."}
          </p>
          <DecorativeBar />
          <div style={responsiveGrid}>
            {/* Map your 'services' array here */}
            {services.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
        </div>
      </section>

      <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, #e2e8f0, transparent)', margin: '0 10%' }} />

      {/* --- INDUSTRIAL SECTION --- */}
      <section style={sectionPadding}>
        <div style={containerStyles}>
          <h2 style={sectionTitleStyles}>Industrial <span style={{ color: BRAND_BLUE }}>Specializations</span></h2>
          <div style={responsiveGrid}>
            {/* Map your 'additionalServices' array here */}
            {additionalServices.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
        </div>
      </section>

      {/* --- JOIN US CTA --- */}
      <section style={{ padding: 'clamp(50px, 10vw, 80px) 0', background: '#fff' }}>
        <div style={containerStyles}>
          <div style={{
            background: '#0f172a',
            borderRadius: '30px',
            padding: 'clamp(40px, 8vw, 80px)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: -100, left: -100, width: '300px', height: '300px',
              background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)',
              borderRadius: '50%'
            }} />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              style={{ position: 'relative', zIndex: 1 }}
            >
              <h2 style={{
                fontSize: 'clamp(2rem, 6vw, 3rem)',
                fontWeight: 900,
                color: '#fff',
                marginBottom: '20px',
                lineHeight: 1.1
              }}>
                Looking for <span style={{ color: '#3b82f6' }}>Manpower Work?</span>
              </h2>
              <p style={{
                fontSize: 'clamp(16px, 2vw, 18px)',
                color: '#94a3b8',
                marginBottom: '40px',
                maxWidth: '600px',
                margin: '0 auto 40px'
              }}>
                We are always hiring skilled carpenters, masons, electricians, and helpers.
                Apply now to join our growing team.
              </p>

              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <motion.a
                  href="/contact"
                  whileHover={{ y: -5 }}
                  style={{
                    background: '#3b82f6', color: '#fff',
                    padding: '18px 40px', borderRadius: '16px',
                    fontWeight: 700, textDecoration: 'none',
                    boxShadow: '0 20px 40px -10px rgba(59,130,246,0.5)'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    // Using window.history to pass state if standard Link isn't available, 
                    // or standard navigation with state if using React Router.
                    // Since we are in a React component, we should ideally use useNavigate.
                    // But to be safe with existing imports:
                    window.history.pushState({ activeTab: 'career' }, '', '/contact');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                    // Fallback refresh if router doesn't pick it up (though standard Link is better)
                    window.location.href = '/contact';
                  }}
                >
                  Apply For A Job
                </motion.a>
              </div>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '20px' }}>
                Click above and select the <strong>"Join Our Team"</strong> tab.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <Cta />
    </main>
  );
};

// --- RESPONSIVE CSS-IN-JS ---
const containerStyles = {
  maxWidth: '1280px',
  margin: '0 auto',
  padding: '0 5%'
};

const heroStyles = {
  background: '#a1a57fcc',
  padding: 'clamp(80px, 15vw, 140px) 0',
  textAlign: 'center'
};

const responsiveGrid = {
  display: 'grid',
  // Key fix: min(100%, 310px) ensures cards never push outside mobile screens
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 310px), 1fr))',
  gap: 'clamp(16px, 3vw, 30px)',
  width: '100%'
};

const sectionTitleStyles = {
  fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
  fontWeight: 900,
  color: DARK_SLATE,
  marginBottom: '40px',
  textAlign: 'center'
};

const heroTitleStyles = {
  fontSize: 'clamp(2.2rem, 7vw, 3.5rem)',
  fontWeight: 900,
  color: DARK_SLATE,
  letterSpacing: '-1px'
};

const heroSubtitleStyles = {
  fontSize: 'clamp(14px, 2vw, 18px)',
  color: '#64748b',
  maxWidth: '700px',
  margin: '20px auto',
  lineHeight: '1.6'
};

const badgeStyles = {
  background: `${BRAND_BLUE}12`, color: BRAND_BLUE, padding: '6px 14px',
  borderRadius: '30px', fontSize: '11px', fontWeight: 800, letterSpacing: '1px',
  marginBottom: '20px', display: 'inline-block'
};

const sectionPadding = { padding: 'clamp(50px, 10vw, 80px) 0' };

const btnStyle = {
  padding: '12px 28px', borderRadius: '12px', fontWeight: 700, fontSize: '15px',
  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px'
};

export default ServicesPage;